import { formatDateTime } from "../dates";
import { alphaMutationRejected } from "../alpha-mutation";
import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router";
import { api } from "../api";
import { ErrorState, Loading } from "../ui/State";
export function ResidentIncidents({ premises }: { premises: string }) {
  const [model, setModel] = useState<any>(),
    [error, setError] = useState<unknown>();
  useEffect(() => {
    let active = true;
    api("/api/portal/leaseholder/" + premises + "/incidents")
      .then((r) => {
        if (active) setModel(r);
      })
      .catch((e) => {
        if (active) setError(e);
      });
    return () => {
      active = false;
    };
  }, [premises]);
  return (
    <section className="panel">
      <h2>Building incident updates</h2>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {!model ? (
        <Loading />
      ) : model.updates.length === 0 ? (
        <p>No published updates.</p>
      ) : (
        model.updates.map((u: any) => (
          <article key={u.id}>
            <p>{u.message}</p>
            <small>{formatDateTime(u.created_at)}</small>
          </article>
        ))
      )}
    </section>
  );
}
export function IncidentsAlpha() {
  const [q] = useSearchParams(),
    building = q.get("building"),
    [list, setList] = useState<any>(),
    [selected, setSelected] = useState(""),
    [model, setModel] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  const pending = useRef<{ url: string; body: any } | null>(null);
  const load = () =>
    building
      ? api("/api/alpha/incidents?building=" + building).then(setList)
      : Promise.resolve();
  useEffect(() => {
    setList(undefined);
    setSelected("");
    setModel(undefined);
    void load().catch(setError);
  }, [building]);
  useEffect(() => {
    setModel(undefined);
    let active = true;
    if (selected)
      api("/api/alpha/incidents/" + selected)
        .then((r) => {
          if (active) setModel(r);
        })
        .catch((e) => {
          if (active) setError(e);
        });
    return () => {
      active = false;
    };
  }, [selected]);
  async function save(url: string, body: any) {
    pending.current ??= {
      url,
      body: { ...body, idempotencyKey: crypto.randomUUID() },
    };
    setBusy(true);
    try {
      const result = await api<any>(pending.current.url, {
        method: "POST",
        body: JSON.stringify(pending.current.body),
      });
      const created = pending.current.url === "/api/alpha/incidents";
      pending.current = null;
      setError(undefined);
      await load();
      if (created) setSelected(result.id);
      else if (selected)
        setModel(await api("/api/alpha/incidents/" + selected));
    } catch (e) {
      if (alphaMutationRejected(e)) pending.current = null;
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <h1>Building incidents</h1>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {pending.current && !busy && (
        <button onClick={() => void save("", {})}>Retry the same save</button>
      )}
      {!building ? (
        <p>Choose a building.</p>
      ) : !list ? (
        <Loading />
      ) : (
        <>
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              void save("/api/alpha/incidents", {
                building,
                title: f.get("title"),
                detail: f.get("detail"),
                priority: f.get("priority"),
              });
            }}
          >
            <fieldset disabled={busy || !!pending.current}>
              <legend>Open an incident</legend>
              <label>
                Title
                <input name="title" required minLength={5} maxLength={200} />
              </label>
              <label>
                Initial details
                <textarea
                  name="detail"
                  required
                  minLength={10}
                  maxLength={5000}
                />
              </label>
              <label>
                Priority
                <select name="priority">
                  <option>Normal</option>
                  <option>High</option>
                  <option>Urgent</option>
                </select>
              </label>
              <button>Open incident</button>
            </fieldset>
          </form>
          <label>
            Incident
            <select
              aria-label="Incident"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">Choose an incident</option>
              {list.incidents.map((i: any) => (
                <option key={i.id} value={i.id}>
                  {i.title} · {i.status}
                </option>
              ))}
            </select>
          </label>
          {model && (
            <section className="panel">
              <h2>{model.incident.title}</h2>
              <p>{model.incident.description}</p>
              <Link to={"/work/cases/" + selected + "?building=" + building}>
                Open work, assignments and completion
              </Link>
              <h3>Related reports</h3>
              {model.reports.map((r: any) => (
                <p key={r.id}>
                  {r.title} · {r.status}
                </p>
              ))}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void save("/api/alpha/incidents/" + selected + "/reports", {
                    caseId: new FormData(e.currentTarget).get("case"),
                  });
                }}
              >
                <fieldset disabled={busy || !!pending.current}>
                  <legend>Link report</legend>
                  <label>
                    Existing case
                    <select name="case" required>
                      <option value="">Choose a report</option>
                      {list.cases.map((r: any) => (
                        <option key={r.id} value={r.id}>
                          {r.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button>Link report</button>
                </fieldset>
              </form>
              <form
                className="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  const f = new FormData(e.currentTarget);
                  void save("/api/alpha/incidents/" + selected + "/updates", {
                    message: f.get("message"),
                    visibility: f.get("visibility"),
                  });
                }}
              >
                <fieldset disabled={busy || !!pending.current}>
                  <legend>Add update</legend>
                  <label>
                    Update
                    <textarea
                      name="message"
                      required
                      minLength={5}
                      maxLength={4000}
                    />
                  </label>
                  <label>
                    Share with
                    <select name="visibility">
                      <option value="Staff">Staff only</option>
                      <option value="Residents">
                        Residents through their portal
                      </option>
                    </select>
                  </label>
                  <button>Save and share as selected</button>
                </fieldset>
              </form>
              {model.updates.map((u: any) => (
                <article key={u.id}>
                  <strong>{u.visibility}</strong>
                  <p>{u.message}</p>
                  <small>{formatDateTime(u.created_at)}</small>
                </article>
              ))}
            </section>
          )}
        </>
      )}
    </>
  );
}
